// filepath: frontend/src/components/TableCellComponent.jsx
import React, { useEffect, useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function TableCellComponent({
  cellValue,
  isFrozen,
  tooltipData,
  cellKey,
  onCellChange,
  rowIndex,
  colIndex,
  hoveredTooltip,
  setHoveredTooltip,
  isAutoFilling,
  stickyStyle,
  isColumnSelected, // New prop
}) {
  const [isEditing, setIsEditing] = useState(false);
  const textareaRef = useRef(null);
  const hasTooltip = tooltipData?.enabled && tooltipData?.text;
  const showTooltip = hoveredTooltip && hoveredTooltip.key === cellKey;

  // Check if cell contains markdown syntax
  // More sophisticated detection that handles lists properly
  const hasMarkdown = cellValue && (() => {
    const value = String(cellValue);
    // Check for markdown patterns
    return (
      value.includes('**') || // Bold
      (value.includes('*') && value.match(/^\s*\*\s+/m)) || // Unordered list with *
      value.includes('`') || // Code
      value.includes('[') || // Links
      value.includes('#') || // Headers
      value.match(/^\s*[-+]\s+/m) || // Unordered lists (starts with - or +)
      value.match(/^\s*\d+\.\s+/m) || // Ordered lists (starts with number.)
      value.includes('|') || // Tables
      value.includes('\n') // Multiline
    );
  })();

  // Preprocess cell value to handle escaped newlines and ensure proper list formatting
  const preprocessMarkdown = (value) => {
    if (!value) return '';
    // Replace escaped newlines with actual newlines
    let processed = value.replace(/\\n/g, '\n');
    
    // Ensure lists have proper spacing (markdown needs blank line before lists)
    // But don't add if it's already there
    processed = processed.replace(/([^\n])\n([-*+]\s|^\d+\.\s)/gm, '$1\n\n$2');
    
    return processed;
  };

  const handleMouseEnter = () => {
    if (hasTooltip) {
      setHoveredTooltip({ key: cellKey, text: tooltipData.text });
    }
  };

  const handleMouseLeave = () => {
    setHoveredTooltip(null);
  };

  const handleEditStart = () => {
    if (!isFrozen && !isAutoFilling) {
      setIsEditing(true);
    }
  };

  const handleEditEnd = (value) => {
    setIsEditing(false);
    if (onCellChange && value !== cellValue) {
      onCellChange(rowIndex, colIndex, value);
    }
  };

  const handleKeyDown = (e) => {
    // Handle Escape to cancel editing
    if (e.key === 'Escape') {
      setIsEditing(false);
      e.preventDefault();
    }
    // Handle Enter (without Shift) to save and exit
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (textareaRef.current) {
        handleEditEnd(textareaRef.current.value);
      }
    }
    // Stop propagation to prevent table-level keyboard handlers
    e.stopPropagation();
  };

  // Auto-focus and auto-resize textarea when editing starts
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      // Move cursor to end
      const length = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(length, length);
      
      // Auto-resize textarea to fit content
      const resizeTextarea = () => {
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
          const scrollHeight = textareaRef.current.scrollHeight;
          // Set min height and max height
          textareaRef.current.style.height = `${Math.max(60, Math.min(scrollHeight, 300))}px`;
        }
      };
      
      resizeTextarea();
      textareaRef.current.addEventListener('input', resizeTextarea);
      
      return () => {
        if (textareaRef.current) {
          textareaRef.current.removeEventListener('input', resizeTextarea);
        }
      };
    }
  }, [isEditing]);

  return (
    <td
      data-cell-key={cellKey}
      className={`relative px-6 py-2 text-base text-gray-700 align-top dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-500 ${stickyStyle ? '' : 'min-w-[150px] w-[150px]'} ${
        (isFrozen || isAutoFilling) 
          ? 'bg-gray-100 text-gray-500 cursor-default dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600'
          : isColumnSelected
          ? 'focus:outline-none focus:ring-2 focus:ring-blue-400 focus:bg-blue-100 hover:bg-blue-100 dark:focus:ring-blue-400 dark:focus:bg-blue-100 dark:hover:bg-blue-800 dark:text-gray-500 dark:ring-gray-500'
          : 'focus:outline-none focus:ring-2 focus:ring-[#51c0a6] focus:bg-blue-50 hover:bg-gray-50 dark:focus:ring-[#51c0a6] dark:focus:bg-customGreen dark:hover:bg-gray-800 dark:hover:text-gray-200'
      }`}
      style={stickyStyle}
      onClick={(e) => {
        e.stopPropagation();
        if (!isFrozen && !isAutoFilling && !isEditing) {
          handleEditStart();
        }
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex items-center min-h-[24px]">
        {isAutoFilling ? (
          <div className="flex-1 flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-black mr-2 dark:border-gray-600 dark:border-t-white"></div>
            <span className="text-gray-400 dark:text-gray-200">Generating...</span>
          </div>
        ) : isEditing ? (
          <textarea
            ref={textareaRef}
            defaultValue={cellValue || ''}
            onBlur={(e) => handleEditEnd(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 w-full min-h-[60px] max-h-[300px] bg-transparent resize-none focus:outline-none text-sm text-gray-700 dark:text-gray-200 border-none p-0 m-0 overflow-y-auto"
            style={{ 
              fontFamily: 'inherit',
              fontSize: 'inherit',
              lineHeight: '1.5',
              height: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          />
        ) : (
          <div className="flex-1 cursor-text" onClick={handleEditStart}>
            {hasMarkdown ? (
              <div className="prose prose-sm max-w-none dark:prose-invert 
                prose-p:my-1 prose-p:leading-tight 
                prose-headings:my-1 prose-headings:leading-tight 
                prose-ul:my-1 prose-ul:pl-4
                prose-ol:my-1 prose-ol:pl-4
                prose-li:my-0.5 prose-li:leading-tight
                prose-code:text-xs prose-code:bg-gray-100 prose-code:dark:bg-gray-800 prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                prose-pre:text-xs prose-pre:p-1 prose-pre:my-1
                prose-blockquote:my-1 prose-blockquote:border-l-2 prose-blockquote:pl-2">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {preprocessMarkdown(cellValue)}
                </ReactMarkdown>
              </div>
            ) : (
              <span className="whitespace-pre-wrap">{cellValue || ''}</span>
            )}
          </div>
        )}
      </div>
      {showTooltip && !isAutoFilling && !isEditing && (
        <div
          className="quill-tooltip-container absolute z-10 px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg shadow-sm dark:bg-gray-700 transition-opacity duration-300 opacity-100 dark:text-gray-700 dark:border-gray-600"
          style={{
            top: '50%',
            left: '100%',
            transform: 'translateY(-50%) translateX(10px)',
            minWidth: '300px',
            pointerEvents: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {tooltipData.text}
          <div className="tooltip-arrow" data-popper-arrow style={{
            position: 'absolute',
            top: '50%',
            right: '100%',
            transform: 'translateY(-50%)',
            width: '0',
            height: '0',
            borderTop: '5px solid transparent',
            borderBottom: '5px solid transparent',
            borderRight: '5px solid rgb(17 24 39 / var(--tw-bg-opacity))',
          }}></div>
        </div>
      )}
    </td>
  );
}

export default TableCellComponent;